/**
 * Stock categories for brick / tile factories (raw materials + finished goods).
 */
export const FACTORY_STOCK_CATEGORIES = [
  'Raw materials — Cement',
  'Raw materials — Sand',
  'Raw materials — Aggregates / ballast',
  'Raw materials — Fuel / firewood',
  'Raw materials — Chemicals & additives',
  'Matofali tayari kuuza',
  'Tiles & pavers',
  'Packaging — pallets & straps',
  'Spare parts & tools',
  'Other factory stock',
];

/** Raw inputs only — used on Add raw material and material orders. */
export const FACTORY_RAW_MATERIAL_CATEGORIES = FACTORY_STOCK_CATEGORIES.filter((c) =>
  String(c).toLowerCase().startsWith('raw materials')
);

/** @returns {{ value: string, label: string }[]} */
export function factoryCategorySelectOptions() {
  return [
    { value: '', label: 'Select category…' },
    ...FACTORY_STOCK_CATEGORIES.map((c) => ({ value: c, label: c })),
  ];
}

/** @returns {{ value: string, label: string }[]} */
export function factoryRawMaterialCategorySelectOptions() {
  return [
    { value: '', label: 'Select raw material type…' },
    ...FACTORY_RAW_MATERIAL_CATEGORIES.map((c) => ({ value: c, label: c })),
  ];
}

/** Finished goods sold at POS / delivery — not raw inputs. */
export const FACTORY_READY_BRICKS_CATEGORY = 'Matofali tayari kuuza';

export const FACTORY_SELLABLE_CATEGORIES = [FACTORY_READY_BRICKS_CATEGORY, 'Tiles & pavers', 'Finished bricks'];

/** Hint for quantity field based on category. */

export function isFactoryRawMaterialCategory(category) {
  return String(category || '')
    .toLowerCase()
    .startsWith('raw materials');
}

export function isFactoryCementCategory(category) {
  const c = String(category || '').toLowerCase();
  return c.startsWith('raw materials') && c.includes('cement');
}

export const FACTORY_POS_SALE_KINDS = {
  bricks: 'bricks',
  cement: 'cement',
};

/** Selling price applies only to finished goods; raw materials are cost-only. */
export function factoryShowsSellingPrice(category) {
  const c = String(category || '').trim().toLowerCase();
  if (!c) return false;
  if (isFactoryCementCategory(category)) return true;
  if (c.startsWith('raw materials')) return false;
  if (FACTORY_SELLABLE_CATEGORIES.some((x) => x.toLowerCase() === c)) return true;
  if (/\bbrick|\btile|\bpaver/.test(c)) return true;
  return false;
}

/** Short label for table chips (full category in title). */
export function factoryCategoryShort(category) {
  const c = String(category || '').trim();
  if (!c) return '—';
  const raw = /^raw materials\s*[—–-]\s*/i;
  if (raw.test(c)) return c.replace(raw, '');
  if (/^finished bricks$/i.test(c)) return 'Matofali (legacy)';
  if (/^matofali tayari kuuza$/i.test(c)) return 'Tayari kuuza';
  return c.length > 22 ? `${c.slice(0, 20)}…` : c;
}

export function factoryCategoryKind(category) {
  if (isFactoryRawMaterialCategory(category)) return 'raw';
  if (factoryShowsSellingPrice(category)) return 'finished';
  return 'other';
}

/** Ready bricks/tiles for POS — not cement, sand, or other inputs. */
export function factoryProductIsPosSellableBricks(product) {
  if (!product) return false;
  if (product.posSaleKind === 'bricks') return true;
  if (product.posSaleKind === 'cement') return false;
  if (isFactoryRawMaterialCategory(product.category)) return false;
  if (!factoryShowsSellingPrice(product.category)) return false;
  if ((Number(product.quantity) || 0) <= 0) return false;
  if ((Number(product.sellingPrice) || 0) > 0) return true;
  return product.brickTypeId != null;
}

/** Cement bags for POS counter sale. */
export function factoryProductIsPosSellableCement(product) {
  if (!product) return false;
  if (product.posSaleKind === 'cement') return true;
  if (product.posSaleKind === 'bricks') return false;
  if (!isFactoryCementCategory(product.category)) return false;
  if ((Number(product.quantity) || 0) <= 0) return false;
  return (Number(product.sellingPrice) || 0) > 0;
}

export function factoryPosProductsForKind(products, kind) {
  const list = Array.isArray(products) ? products : [];
  if (kind === FACTORY_POS_SALE_KINDS.cement) {
    return list.filter((p) => factoryProductIsPosSellableCement(p));
  }
  return list.filter((p) => factoryProductIsPosSellableBricks(p));
}

/** Any factory POS sellable (bricks or cement). */
export function factoryProductIsPosSellable(product) {
  return factoryProductIsPosSellableBricks(product) || factoryProductIsPosSellableCement(product);
}

/** Display/save unit for factory raw materials — infers from category when legacy data used pcs. */
export function resolveFactoryMaterialUnit(product) {
  const category = product?.category;
  const stored = String(product?.unit || '').toLowerCase().trim();
  if (stored && stored !== 'pcs') return stored;
  if (isFactoryRawMaterialCategory(category)) return factoryDefaultUnit(category);
  return stored || 'pcs';
}

/** Suggested unit when registering a raw material by category. */
export function factoryDefaultUnit(category) {
  const c = String(category || '').toLowerCase();
  if (c.includes('cement')) return 'bag';
  if (c.includes('sand')) return 'lorry';
  if (c.includes('aggregates') || c.includes('ballast')) return 'tonne';
  if (c.includes('fuel') || c.includes('firewood')) return 'litre';
  if (c.includes('chemical')) return 'kg';
  return 'bag';
}

export function factoryQuantityHint(category) {
  const c = String(category || '').toLowerCase();
  if (c.includes('cement')) return 'Usually in bags (e.g. 50 kg bags).';
  if (c.includes('sand')) return 'Usually counted in lorry loads (gari la mchanga).';
  if (c.includes('aggregates') || c.includes('ballast')) return 'Tonnes (tone) or kilograms (kg).';
  if (c.includes('fuel') || c.includes('firewood')) return 'Litres, bundles, or bags.';
  if (c.includes('brick') || c.includes('tile') || c.includes('paver')) return 'Pieces (pcs).';
  if (c.includes('pallet')) return 'Pallets or bundles.';
  return 'Enter count in your usual unit for this item.';
}

export function mergedFactoryCategories(products) {
  const fromDb = Array.from(
    new Set((products || []).map((p) => (p.category || '').trim()).filter(Boolean))
  );
  const catalog = new Set(FACTORY_STOCK_CATEGORIES);
  const extras = fromDb.filter((c) => !catalog.has(c));
  return [...FACTORY_STOCK_CATEGORIES, ...extras].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}
